import { Component, computed, input, model, signal } from "@angular/core";
import { EChartsOption } from "echarts/types/dist/shared";

import {
  SegyFieldOption,
  SegyGridBox,
  SegyScanResponse,
} from "../../../../models/segy";

/** Which position sets are drawn. */
export interface GeometryLayers {
  sources: boolean;
  receivers: boolean;
  cdps: boolean;
}

export const DEFAULT_GRID_BOX: SegyGridBox = {
  xOrigin: null,
  yOrigin: null,
  nx1: null,
  nx2: null,
  dx: 50,
  angle: 0,
  xyscale: 1,
};

const LAYERS = [
  { key: "sources", name: "Sources", x: "soux", y: "souy", symbol: "circle", size: 7, color: "#1890ff" },
  { key: "receivers", name: "Receivers", x: "recx", y: "recy", symbol: "rect", size: 4, color: "#bfbfbf" },
  { key: "cdps", name: "CDPs", x: "cdpx", y: "cdpy", symbol: "diamond", size: 3, color: "#95de64" },
] as const;

type Point = [number, number, number | null, number];

/** The gather shown on the Display tab, marked on the map: its source(s),
 * its receivers and the clicked trace. Coordinates in the scan's units. */
export interface GeometryHighlight {
  label: string;
  /** [x, y, trace] */
  sources: [number, number, number][];
  receivers: [number, number, number][];
  selected: { source: [number, number]; receiver: [number, number]; trace: number } | null;
}

/** A grid box resolved to world coordinates (null while incomplete). */
interface ResolvedBox {
  corners: [number, number][];
  origin: [number, number];
  /** world unit vectors along NX1 / NX2 */
  u1: [number, number];
  u2: [number, number];
  /** NX1 / NX2 extents in coordinate units */
  len1: number;
  len2: number;
}

/**
 * Source / receiver / CDP map built from the shared header scan (one pass,
 * real-unit coordinates), coloured by any header value (e.g. source depth,
 * receiver elevation), with a model grid box overlay in the preprep
 * convention (origin, NX1 × NX2 nodes at DX, rotated by ANGLE) that can be
 * typed, picked on the map, fitted to the data or copied out as parameters.
 */
@Component({
  selector: "app-segy-geometry-map",
  templateUrl: "./geometry-map.component.html",
  styleUrl: "./geometry-map.component.less",
  standalone: false,
})
export class SegyGeometryMapComponent {
  public readonly scan = input<SegyScanResponse | null>(null);
  public readonly fieldOptions = input<SegyFieldOption[]>([]);
  public readonly colorField = model<string | null>(null);
  public readonly layers = model<GeometryLayers>({
    sources: true,
    receivers: true,
    cdps: false,
  });
  public readonly box = model<SegyGridBox>(DEFAULT_GRID_BOX);
  public readonly highlight = input<GeometryHighlight | null>(null);
  /** Draw the displayed gather on top of the scan. */
  public readonly showHighlight = model<boolean>(true);

  /** Fit also searches the rotation (tightest rectangle) instead of using
   * the angle typed in. */
  public readonly fitAngle = model<boolean>(false);
  /** Margin (model units, like DX) added round the data on fit, and the step
   * of the pad / trim buttons. */
  public readonly padding = model<number>(500);

  protected readonly boxMessage = signal<string | null>(null);
  protected readonly layerDefs = LAYERS;

  /** CDP coordinates are often unset in raw shot files. */
  protected readonly hasCdps = computed(() => {
    const f = this.scan()?.fields;
    return !!f?.["cdpx"]?.some((v, i) => v !== 0 || f["cdpy"][i] !== 0);
  });

  /** Deduplicated points per layer: [x, y, colour value, first trace]. */
  private readonly points = computed(() => {
    const scan = this.scan();
    const out: Record<string, Point[]> = {};
    if (!scan) return out;
    const field = this.colorField();
    const colour =
      field === "trace" ? scan.trace : field ? scan.fields[field] : null;
    for (const layer of LAYERS) {
      const xs = scan.fields[layer.x];
      const ys = scan.fields[layer.y];
      if (!xs || !ys) continue;
      const seen = new Map<string, Point>();
      for (let i = 0; i < xs.length; i++) {
        const c = colour ? colour[i] : null;
        const k = `${xs[i]},${ys[i]},${c}`;
        if (!seen.has(k)) seen.set(k, [xs[i], ys[i], c, scan.trace[i]]);
      }
      out[layer.key] = [...seen.values()];
    }
    return out;
  });

  protected readonly counts = computed(() => {
    const p = this.points();
    return {
      sources: new Set((p["sources"] ?? []).map((q) => `${q[0]},${q[1]}`)).size,
      receivers: new Set((p["receivers"] ?? []).map((q) => `${q[0]},${q[1]}`))
        .size,
    };
  });

  protected readonly resolvedBox = computed<ResolvedBox | null>(() =>
    resolveBox(this.box()),
  );

  /** How many shown positions fall inside the box, per layer. */
  protected readonly boxCoverage = computed(() => {
    const box = this.resolvedBox();
    if (!box) return [];
    const shown = this.layers();
    const p = this.points();
    return LAYERS.filter((l) => shown[l.key] && p[l.key]?.length).map((l) => {
      const pts = p[l.key];
      const inside = pts.filter((q) => insideBox(box, q[0], q[1])).length;
      return { name: l.name, inside, total: pts.length };
    });
  });

  protected readonly options = computed<EChartsOption | null>(() =>
    this.buildOptions(),
  );

  // ─────────────────────────────────────────────────────────────
  // BOX CONTROLS
  // ─────────────────────────────────────────────────────────────

  protected setBox(key: keyof SegyGridBox, value: any) {
    this.boxMessage.set(null);
    this.box.update((b) => ({ ...b, [key]: value }));
  }

  protected setLayer(key: keyof GeometryLayers, on: boolean) {
    this.layers.update((l) => ({ ...l, [key]: on }));
  }

  protected clearBox() {
    this.box.set({ ...DEFAULT_GRID_BOX });
    this.boxMessage.set(null);
  }

  /** Smallest box at the current angle + DX enclosing every shown point. */
  protected fitBoxToData() {
    const b = this.box();
    if (!b.dx || b.dx <= 0) {
      this.boxMessage.set("Set DX first — the fit sizes NX1 / NX2 from it.");
      return;
    }
    const shown = this.layers();
    const pts = LAYERS.filter((l) => shown[l.key]).flatMap(
      (l) => this.points()[l.key] ?? [],
    );
    if (!pts.length) {
      this.boxMessage.set("No points shown to fit the box to.");
      return;
    }
    const angle = this.fitAngle() ? round3(minAreaAngle(pts)) : b.angle;
    const a = (angle * Math.PI) / 180;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
    for (const [x, y] of pts) {
      const u = x * cos + y * sin;
      const v = -x * sin + y * cos;
      uMin = Math.min(uMin, u);
      uMax = Math.max(uMax, u);
      vMin = Math.min(vMin, v);
      vMax = Math.max(vMax, v);
    }
    const step = b.dx / (b.xyscale || 1); // node spacing in coordinate units
    const pad = Math.max(0, Math.round((this.padding() || 0) / b.dx)) * step;
    uMin -= pad;
    vMin -= pad;
    uMax += pad;
    vMax += pad;
    this.box.set({
      ...b,
      angle,
      xOrigin: round3(uMin * cos - vMin * sin),
      yOrigin: round3(uMin * sin + vMin * cos),
      nx1: Math.ceil((uMax - uMin) / step) + 1,
      nx2: Math.ceil((vMax - vMin) / step) + 1,
    });
    this.boxMessage.set(
      this.fitAngle() ? `Fitted angle ${fmtNum(angle)}°.` : null,
    );
  }

  /** Grow (+1) or shrink (−1) the box by the padding on every side, keeping
   * DX and the angle (whole nodes, so the grid stays aligned). */
  protected padBox(direction: 1 | -1) {
    const b = this.box();
    const box = this.resolvedBox();
    if (!box || !b.dx) return;
    const nodes = Math.max(1, Math.round((this.padding() || 0) / b.dx));
    const n = direction * nodes;
    if (b.nx1! + 2 * n < 2 || b.nx2! + 2 * n < 2) {
      this.boxMessage.set("The box is too small to trim further.");
      return;
    }
    const step = b.dx / (b.xyscale || 1);
    const shift = n * step;
    this.box.set({
      ...b,
      xOrigin: round3(b.xOrigin! - (box.u1[0] + box.u2[0]) * shift),
      yOrigin: round3(b.yOrigin! - (box.u1[1] + box.u2[1]) * shift),
      nx1: b.nx1! + 2 * n,
      nx2: b.nx2! + 2 * n,
    });
    this.boxMessage.set(null);
  }

  /** preprep / brute_stack model_parameters for the current box. */
  protected readonly boxParams = computed<string | null>(() => {
    const b = this.box();
    if (!this.resolvedBox()) return null;
    return JSON.stringify(
      {
        XORIGIN: b.xOrigin,
        YORIGIN: b.yOrigin,
        XYSCALE: b.xyscale,
        NX1: b.nx1,
        NX2: b.nx2,
        DX: b.dx,
        ANGLE: b.angle,
      },
      null,
      2,
    );
  });

  protected async copyParams() {
    const text = this.boxParams();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      this.boxMessage.set("Copied model parameters to the clipboard.");
    } catch {
      this.boxMessage.set("Could not access the clipboard.");
    }
  }

  // ─────────────────────────────────────────────────────────────
  // CHART
  // ─────────────────────────────────────────────────────────────

  private buildOptions(): EChartsOption | null {
    const scan = this.scan();
    if (!scan) return null;
    const shown = this.layers();
    const points = this.points();
    const field = this.colorField();
    const coloured = !!field && LAYERS.some((l) => points[l.key]?.[0]?.[2] != null);

    const series: any[] = [];
    const pointSeries: number[] = [];
    let cMin = Infinity;
    let cMax = -Infinity;
    for (const layer of LAYERS) {
      const data = points[layer.key];
      if (!shown[layer.key] || !data?.length) continue;
      if (layer.key === "cdps" && !this.hasCdps()) continue;
      if (coloured) {
        for (const p of data) {
          if (p[2] == null) continue;
          if (p[2] < cMin) cMin = p[2];
          if (p[2] > cMax) cMax = p[2];
        }
      }
      pointSeries.push(series.length);
      series.push({
        name: layer.name,
        type: "scatter",
        animation: false,
        symbol: layer.symbol,
        symbolSize: layer.size,
        color: layer.color,
        // large mode can't take per-point colours from the visualMap
        large: !coloured,
        progressive: 20000,
        data,
        z: layer.key === "sources" ? 3 : 2,
      });
    }

    const box = this.resolvedBox();
    const extentPts: number[][] = [];
    for (const i of pointSeries) extentPts.push(...series[i].data);
    if (box) {
      extentPts.push(...box.corners);
      series.push(
        {
          name: "Model box",
          type: "line",
          data: [...box.corners, box.corners[0]],
          showSymbol: false,
          lineStyle: { color: "#ff4d4f", width: 2 },
          areaStyle: { color: "rgba(255,77,79,0.05)" },
          animation: false,
          silent: true,
          z: 4,
        },
        this.axisSeries("NX1", box.origin, box.u1, box.len1),
        this.axisSeries("NX2", box.origin, box.u2, box.len2),
        {
          name: "Origin",
          type: "scatter",
          data: [box.origin],
          symbolSize: 11,
          itemStyle: { color: "#ff4d4f", borderColor: "#fff", borderWidth: 1 },
          animation: false,
          z: 6,
        },
      );
    }

    const hl = this.showHighlight() ? this.highlight() : null;
    if (hl) {
      const name = `Displayed gather`;
      series.push(
        {
          name,
          type: "scatter",
          data: hl.receivers.map((r) => [r[0], r[1], null, r[2]]),
          symbol: "rect",
          symbolSize: 6,
          itemStyle: { color: "#fa541c" },
          animation: false,
          z: 7,
        },
        {
          name,
          type: "scatter",
          data: hl.sources.map((r) => [r[0], r[1], null, r[2]]),
          symbol: "pin",
          symbolSize: 26,
          itemStyle: { color: "#cf1322", borderColor: "#fff", borderWidth: 1 },
          animation: false,
          z: 9,
        },
      );
      if (hl.selected) {
        const { source, receiver, trace } = hl.selected;
        series.push({
          name: "Selected trace",
          type: "line",
          data: [
            [source[0], source[1], null, trace],
            [receiver[0], receiver[1], null, trace],
          ],
          symbol: "circle",
          symbolSize: 10,
          lineStyle: { color: "#722ed1", width: 2, type: "dashed" },
          itemStyle: { color: "#722ed1", borderColor: "#fff", borderWidth: 2 },
          animation: false,
          z: 10,
        });
      }
    }

    const bounds = squareBounds(extentPts);
    const fieldLabel = field === "trace" ? "trace" : field;
    return {
      animation: false,
      legend: {
        top: 0,
        data: [
          ...new Set(
            series.filter((s) => !s.name.startsWith("NX")).map((s) => s.name),
          ),
        ],
      },
      grid: { left: 10, right: coloured ? 90 : 20, top: 30, bottom: 40, containLabel: true },
      tooltip: {
        trigger: "item",
        formatter: (p: any) => {
          const [x, y, c, trace] = p.value ?? [];
          let html = `<b>${p.seriesName}</b><br/>x: ${fmtNum(x)}<br/>y: ${fmtNum(y)}`;
          if (coloured && c != null) html += `<br/>${fieldLabel}: ${fmtNum(c)}`;
          if (trace != null) html += `<br/><span style="color:#999">trace ${trace}</span>`;
          return html;
        },
      },
      ...(coloured && isFinite(cMin)
        ? {
            visualMap: {
              type: "continuous",
              dimension: 2,
              seriesIndex: pointSeries,
              min: cMin,
              max: cMax === cMin ? cMin + 1 : cMax,
              calculable: true,
              precision: 2,
              right: 0,
              top: "middle",
              itemHeight: 200,
              text: [fieldLabel ?? "", ""],
              inRange: {
                color: ["#440154", "#3b528b", "#21918c", "#5ec962", "#fde725"],
              },
            },
          }
        : {}),
      xAxis: {
        type: "value",
        name: "X (easting)",
        nameLocation: "middle",
        nameGap: 25,
        min: bounds.xMin,
        max: bounds.xMax,
        axisLabel: { formatter: (v: number) => fmtNum(v) },
      },
      yAxis: {
        type: "value",
        name: "Y (northing)",
        nameLocation: "middle",
        nameGap: 60,
        min: bounds.yMin,
        max: bounds.yMax,
        axisLabel: { formatter: (v: number) => fmtNum(v) },
      },
      dataZoom: [
        { type: "inside", xAxisIndex: 0, filterMode: "none" },
        { type: "inside", yAxisIndex: 0, filterMode: "none" },
      ],
      series,
    } as EChartsOption;
  }

  /** A labelled arrow-ish line from the origin along one grid axis. */
  private axisSeries(
    name: string,
    origin: [number, number],
    unit: [number, number],
    length: number,
  ) {
    const end = [origin[0] + unit[0] * length, origin[1] + unit[1] * length];
    return {
      name,
      type: "line",
      data: [
        origin,
        {
          value: end,
          symbol: "arrow",
          symbolSize: 10,
          symbolRotate: (Math.atan2(unit[1], unit[0]) * 180) / Math.PI - 90,
          label: { show: true, formatter: name, color: "#cf1322", fontWeight: 600 },
        },
      ],
      showSymbol: true,
      symbol: "none",
      lineStyle: { color: "#cf1322", width: 2.5 },
      itemStyle: { color: "#cf1322" },
      animation: false,
      silent: true,
      z: 5,
    };
  }

  protected readonly fmtNum = fmtNum;
}

/** World-space corners/axes of a complete grid box, or null. */
function resolveBox(b: SegyGridBox): ResolvedBox | null {
  if (
    b.xOrigin == null ||
    b.yOrigin == null ||
    !b.nx1 ||
    !b.nx2 ||
    !b.dx ||
    b.dx <= 0
  ) {
    return null;
  }
  const a = ((b.angle ?? 0) * Math.PI) / 180;
  const u1: [number, number] = [Math.cos(a), Math.sin(a)];
  const u2: [number, number] = [-Math.sin(a), Math.cos(a)];
  const step = b.dx / (b.xyscale || 1);
  const len1 = (b.nx1 - 1) * step;
  const len2 = (b.nx2 - 1) * step;
  const o: [number, number] = [b.xOrigin, b.yOrigin];
  const at = (s: number, t: number): [number, number] => [
    o[0] + u1[0] * s + u2[0] * t,
    o[1] + u1[1] * s + u2[1] * t,
  ];
  return {
    corners: [at(0, 0), at(len1, 0), at(len1, len2), at(0, len2)],
    origin: o,
    u1,
    u2,
    len1,
    len2,
  };
}

function insideBox(box: ResolvedBox, x: number, y: number): boolean {
  const dx = x - box.origin[0];
  const dy = y - box.origin[1];
  const s = dx * box.u1[0] + dy * box.u1[1];
  const t = dx * box.u2[0] + dy * box.u2[1];
  const eps = 1e-6 * Math.max(box.len1, box.len2, 1);
  return s >= -eps && s <= box.len1 + eps && t >= -eps && t <= box.len2 + eps;
}

/** Padded extent, squared so easting/northing read aspect-correct. */
function squareBounds(points: number[][]) {
  let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
  for (const p of points) {
    if (p[0] < xMin) xMin = p[0];
    if (p[0] > xMax) xMax = p[0];
    if (p[1] < yMin) yMin = p[1];
    if (p[1] > yMax) yMax = p[1];
  }
  if (!isFinite(xMin)) return { xMin: 0, xMax: 1, yMin: 0, yMax: 1 };
  const half = ((Math.max(xMax - xMin, yMax - yMin) || 1) * 1.08) / 2;
  const xMid = (xMin + xMax) / 2;
  const yMid = (yMin + yMax) / 2;
  return {
    xMin: Math.floor(xMid - half),
    xMax: Math.ceil(xMid + half),
    yMin: Math.floor(yMid - half),
    yMax: Math.ceil(yMid + half),
  };
}

/** Rotation (deg CCW from +X, in [0, 90)) of the minimum-area rectangle
 * round the points: one side of it lies along an edge of the convex hull. */
function minAreaAngle(points: number[][]): number {
  const hull = convexHull(points);
  if (hull.length < 3) return 0;
  let best = 0;
  let bestArea = Infinity;
  for (let i = 0; i < hull.length; i++) {
    const [x0, y0] = hull[i];
    const [x1, y1] = hull[(i + 1) % hull.length];
    const theta = Math.atan2(y1 - y0, x1 - x0);
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
    for (const [x, y] of hull) {
      const u = x * c + y * s;
      const v = -x * s + y * c;
      if (u < uMin) uMin = u;
      if (u > uMax) uMax = u;
      if (v < vMin) vMin = v;
      if (v > vMax) vMax = v;
    }
    const area = (uMax - uMin) * (vMax - vMin);
    if (area < bestArea) {
      bestArea = area;
      best = theta;
    }
  }
  const deg = (best * 180) / Math.PI;
  return ((deg % 90) + 90) % 90;
}

/** Andrew's monotone chain. */
function convexHull(points: number[][]): number[][] {
  const pts = [...new Map(points.map((p) => [`${p[0]},${p[1]}`, [p[0], p[1]]])).values()]
    .sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (pts.length < 3) return pts;
  const cross = (o: number[], a: number[], b: number[]) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: number[][] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: number[][] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

function fmtNum(v: number | null | undefined): string {
  if (v == null || !isFinite(v)) return "—";
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
}
